<template>
  <div
    v-if="card"
    class="cf-flex cf-flex--column cf-flex--gap-16 cf-padding-top-16"
  >
    <Teleport
      defer
      to="#portal-modal-header-target"
    >
      Gift Card Info
    </Teleport>

    <div class="cf-grid cf-grid--2-grid-column-equal">
      <div class="cf-flex cf-flex--column cf-flex--align-start">
        <h4 class="cf-title cf-title--4">
          Gift Card Number
        </h4>

        <button
          v-copy-to-clipboard="{successMessage: 'Gift card number copied to clipboard!', value: card.cardNumber }"
          type="button"
          class="cf-title cf-title--4"
          title="Copy Gift Card Number to Clipboard"
        >
          <template v-if="shouldShowFullCardNumber">
            {{ $cardNumber(card.cardNumber) }}
          </template>

          <template v-else>
            •••• {{ card.cardNumberLastFour }}
          </template>
        </button>
      </div>

      <button
        type="button"
        class="cf-button cf-button--tertiary cf-button--fixed-32 cf-justify-self-end"
        title="Show/Hide Gift Card Number"
        @click="toggleShowFullCardNumber"
      >
        <span :class="['cf-icon far', { 'fa-eye': !shouldShowFullCardNumber, 'fa-eye-slash': shouldShowFullCardNumber }]" />
      </button>
    </div>

    <cf-gift-card
      :card-design="card.design"
      :card-amount="card.amount"
    />

    <div
      v-if="isQrCodeScanningEnabled"
      class="cf-flex cf-flex--align-center cf-flex--justify-center"
    >
      <vue-qrcode
        :value="card.cardNumber"
        :margin="2"
        :scale="5"
      />
    </div>

    <p
      v-if="isAbleToAddFunds || isAbleToMerge"
      class="cf-subtitle cf-subtitle--2"
    >
      You can
      <span v-if="isAbleToAddFunds"> add additional funds</span>
      <span v-if="isAbleToAddFunds && isAbleToMerge"> or</span>
      <span v-if="isAbleToMerge"> merge the balance of two or more gift cards </span>
      below.
    </p>

    <p
      v-else-if="isAbleToRemove"
      class="cf-subtitle cf-subtitle--2"
    >
      Funds cannot be added to this gift card. You can remove it and/or purchase a new card.
    </p>

    <div
      v-if="merchant.giftCardConfiguration.autoReloadEnabled && isAbleToAddFunds"
      class="cf-flex cf-flex--row cf-flex--align-center"
    >
      <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--grow-1 cf-flex--gap-8">
        <span class="'cf-icon cf-icon--heavy far fa-repeat" />

        <div class="cf-flex cf-flex--column cf-text-align-left">
          <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">
            Auto Reload
          </p>

          <p
            v-if="card.autoReload && card.autoReload.enabled"
            class="cf-subtitle cf-subtitle--2"
          >
            Reload <span class="cf-subtitle--heavy">{{ dollars(card.autoReload.reloadAmount, { trimZeros: true }) }}</span>
            when my balance is below <span class="cf-subtitle--heavy">{{ dollars(card.autoReload.thresholdTriggerAmount, { trimZeros: true }) }}</span>
          </p>
        </div>
      </div>

      <button
        type="button"
        class="cf-button cf-button--tertiary cf-button--xs cf-justify-self-end"
        @click="$openModal('giftCardAutoReload', { card })"
      >
        Edit
      </button>
    </div>

    <!-- Merge Card Button -->
    <button
      v-if="isAbleToMerge"
      type="button"
      class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-8 cf-cursor-pointer"
      @click="$openModal('giftCardMerge', { cardId: card.cardId })"
    >
      <svg-icon
        icon-name="merge"
        size="text"
      />

      <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">
        Merge Card
      </p>
    </button>

    <!-- Remove Card Button -->
    <button
      v-if="isAbleToRemove"
      type="button"
      class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-8 cf-cursor-pointer"
      title="Remove Gift Card"
      @click="removeCardClickHandler"
    >
      <span class="cf-icon danger far fa-trash-alt" />

      <p class="cf-subtitle cf-subtitle--2 cf-subtitle--red">
        Delete
      </p>
    </button>

    <div class="cf-modal__footer cf-modal__footer--sticky">
      <!-- Add to Apple Wallet -->
      <button
        v-if="canAddToWallets && supportsAppleWallet"
        :class="['cf-cursor-pointer cf-clear-padding', { 'cf-button--loading': isFetchingAppleWallet }]"
        type="button"
        @click="handleAddToAppleWallet"
      >
        <img
          :class="['cf-image', { 'semi-transparent': isFetchingAppleWallet }]"
          style="height: var(--input-height)"
          src="/images/apple-wallet.svg"
        >
      </button>

      <!-- Add to Google Wallet -->
      <button
        :class="['cf-cursor-pointer cf-clear-padding', { 'cf-button--loading': isFetchingGoogleWallet }]"
        type="button"
        @click="handleAddToGoogleWallet"
      >
        <img
          :class="['cf-image', { 'semi-transparent': isFetchingGoogleWallet }]"
          style="height: var(--input-height)"
          src="/images/google-pay-save.svg"
        >
      </button>

      <button
        v-if="isAbleToAddFunds"
        type="button"
        class="cf-button cf-button--primary cf-button--lg cf-button--fill"
        @click="$openModal('giftCardPurchase', { cardToReload: card })"
      >
        Add Funds
      </button>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';
  import uaParser from 'ua-parser-js';

  import http from '@/services/http';
  import logger from '@/services/logger';

  import { downloadPkpass } from '@/helpers/download-apple-wallet-pass';

  import dollars from '@/filters/dollars';

  import CfGiftCard from '@/view/components/globals/cf-gift-card/index.vue';


  export default {
    name: 'GiftCardDetails',

    components: {
      CfGiftCard
    },

    props: {
      cardId: {
        type: String,
        required: true
      }
    },

    data() {
      return {
        shouldShowFullCardNumber: false,
        isFetchingGoogleWallet: false,
        isFetchingAppleWallet: false
      };
    },

    computed: {
      ...mapGetters('giftCards', ['giftCards']),
      ...mapGetters('merchant', ['merchant']),

      card() {
        return this.giftCards.savedCards.find(c => c.cardId === this.cardId);
      },

      isAbleToAddFunds() {
        return this.merchant.giftCardConfiguration.allowZeroBalanceReloads || this.card.amount > 0;
      },

      isAbleToMerge() {
        const cardCountWithValue = this.giftCards.savedCards.filter(card => card.amount > 0);

        return cardCountWithValue.length > 1 && this.card.amount > 0 && this.merchant.giftCardConfiguration?.allowBalanceMerge;
      },

      isAbleToRemove() {
        return this.card.amount === 0 || !this.card.isVirtual;
      },

      canAddToWallets() {
        return this.isAbleToAddFunds && this.merchant.giftCardConfiguration.walletPassesEnabled && !this.merchant.features['hide-gift-card-save-to-wallet'];
      },

      supportsAppleWallet() {
        const { os, browser } = uaParser(window.navigator.userAgent);
        /** Iphones only */
        return os.name === 'iOS' && browser.name === 'Mobile Safari';
      },

      isQrCodeScanningEnabled() {
        return !this.merchant.features['hide-gift-card-qr-code'];
      }
    },

    async mounted() {
      this.getLatestCardBalance();
    },

    async unmounted() {
      this.getLatestCardBalance();
    },

    methods: {
      dollars,

      getLatestCardBalance() {
        this.$store.dispatch('giftCards/updateCardBalance', this.cardId);
      },

      removeCardClickHandler() {
        const primaryText = this.isAbleToAddFunds
          ? 'You are about to remove this gift card from your account. You can always add it again using the details on your gift card.'
          : 'You are about to remove this gift card from your account.';

        this.$openModal('confirmation', {
          ctaConfirmText: 'Yes, Remove',
          hideSecondaryCta: true,
          primaryText,
          onConfirm: () => this.$store.dispatch('giftCards/removeCard', this.card),
          onConfirmSuccess: () => this.$closeModal('giftCardDetails'),
          title: 'Remove gift card?'
        });
      },

      toggleShowFullCardNumber() {
        this.shouldShowFullCardNumber = !this.shouldShowFullCardNumber;
      },

      async handleAddToAppleWallet() {
        try {
          this.isFetchingAppleWallet = true;

          const { data } = await http.get(
            `/cardfree-api/v1/wallet/gift-card/${this.cardId}/apple`,
            {
              responseType: 'arraybuffer',
              headers: { accept: 'application/vnd.apple.pkpass' }
            }
          );

          downloadPkpass(data, 'merchantGiftCard');
        }
        catch (error) {
          logger.error(error);
        }
        finally {
          this.isFetchingAppleWallet = false;
        }
      },

      async handleAddToGoogleWallet() {
        try {
          this.isFetchingGoogleWallet = true;

          const { data } = await http.get(`/cardfree-api/v1/wallet/gift-card/${this.cardId}/google`);

          window.open(data, '_blank');
        }
        catch (error) {
          logger.error(error);
        }
        finally {
          this.isFetchingGoogleWallet = false;
        }
      }
    }
  };
</script>
